import React, { useState } from 'react';
import { Box, Typography, Button, IconButton, Grid, LinearProgress, Alert, CircularProgress } from '@mui/material';
import { PaperClipIcon, XMarkIcon, CheckCircleIcon, ExclamationTriangleIcon, DocumentTextIcon, PhotoIcon, DocumentIcon, TableCellsIcon, DocumentChartBarIcon, EyeIcon } from '@heroicons/react/24/outline';
import AttachmentUploadButton from '../Button/AttachmentButton/AttachmentUploadButton';
import CustomSnackbar from '../snackbar/CustomSnackbar';
import LocalFileCard from '../Attachment/LocalFileCard';
import { validateFiles } from '../../utils/checkFiles';
import { getPresignedUploadUrl, uploadFileDirectlyToS3 } from '../../utils/api';
import ImagePreviewModal from '../Modal/ImagePreviewModal';
import styles from './TesCaseAddAndEditView.module.scss';
import { useSelector } from 'react-redux';
import { useScanningStatusSSE } from '../../hooks/useScanningStatusSSE';


const FileSelectionWithPreview = ({ onFilesSelected }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState('success');
  const selectedProject = useSelector(state => state.projects.selectedProject)

  // Upload file directly to S3 (JIRA-style)
  const uploadFileDirectly = async (file) => {
    try {
      // Step 1: Get pre-signed URL
      const presignedResult = await getPresignedUploadUrl(
        file.name,
        file.type,
        file.size,
        selectedProject?.org_id,
        selectedProject?.id
      );

      if (presignedResult.status !== 1) {
        return {
          success: false,
          error: presignedResult.error || 'Failed to get upload URL'
        };
      }

      const presignedData = presignedResult.data;

      // Step 2: Upload directly to S3
      const uploadResult = await uploadFileDirectlyToS3(
        file,
        presignedData,
        (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          
          setSelectedFiles(prev => prev.map(f => 
            f.id === file.id 
              ? { ...f, uploadProgress: progress }
              : f
          ));
        }
      );

      if (!uploadResult.success) {
        return {
          success: false,
          error: uploadResult.error || 'Direct upload failed'
        };
      }

      return {
        success: true,
        data: {
          fileKey: presignedData.fileKey,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          status: 'uploaded-to-s3'
        }
      };
    } catch (error) {
      console.error('Error uploading file directly:', error);
      return {
        success: false,
        error: error.message || 'Upload failed'
      };
    }
  };

  // Handle file selection with immediate upload
  const handleFileSelection = async (e) => {
    const files = e.target.files;
    if (!files.length) {
      return;
    }

    const { validFiles, errorMessage, hasErrors } = validateFiles(files);

    if (hasErrors) {
      setMessage(errorMessage);
      setSeverity('error');
      setSnackbarOpen(true);
    }

    // If no valid files after validation, stop the process
    if (validFiles.length === 0) {
      return;
    }

    try {
      // Convert FileList to Array and add initial state
      const filesWithPreviews = Array.from(validFiles).map(file => ({
        id: Math.random().toString(36).substr(2, 9),
        file: file,
        name: file.name,
        size: file.size,
        type: file.type,
        preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
        s3PreviewUrl: null,
        uploading: true,
        uploadProgress: 0,
        uploaded: false,
        error: null,
        fileKey: null,
        tempUrl: null
      }));

      // Add files to state immediately
      setSelectedFiles(prev => {
        const updatedFiles = [...prev, ...filesWithPreviews];
        // Notify parent component that files are now uploading
        onFilesSelected(updatedFiles);
        return updatedFiles;
      });
      
      // Upload each file directly to S3
      for (const fileObj of filesWithPreviews) {
        const result = await uploadFileDirectly(fileObj.file);
        
        if (result.success) {
          // Keep local blob preview until scan is CLEAN — do not fetch S3 URLs for quarantine keys
          setSelectedFiles(prev => {
            const updatedFiles = prev.map(f => 
              f.id === fileObj.id 
                ? {
                    ...f,
                    fileKey: result.data.fileKey,
                    s3PreviewUrl: null,
                    uploading: false,
                    uploadProgress: 100,
                    uploaded: true
                  }
                : f
            );
            
            // Notify parent component of state change
            onFilesSelected(updatedFiles);
            return updatedFiles;
          });
        } else {
          // Handle upload error
          setSelectedFiles(prev => {
            const updatedFiles = prev.map(f => 
              f.id === fileObj.id 
                ? { 
                    ...f, 
                    uploading: false, 
                    uploadProgress: 0,
                    error: result.error
                  }
                : f
            );
            
            // Notify parent component of state change
            onFilesSelected(updatedFiles);
            return updatedFiles;
          });
        }
      }

      // Remove the duplicate onFilesSelected call - it's already called in the loop above

      setMessage(`Successfully Selected ${validFiles.length} File(s)!`);
      setSeverity('success');
      setSnackbarOpen(true);

    } catch (error) {
      console.error('Error processing files:', error);
      setMessage('Failed to process files!');
      setSeverity('error');
      setSnackbarOpen(true);
    } finally {
      // Clear the file input value to allow selecting the same file again
      e.target.value = '';
    }
  };

  // Handle file removal
  const handleRemoveFile = (fileId) => {
    setSelectedFiles(prev => {
      const updatedFiles = prev.filter(f => f.id !== fileId);
      
      // Notify parent component
      onFilesSelected(updatedFiles);
      
      return updatedFiles;
    });
  };

  // Retry failed upload
  const handleRetryUpload = async (fileId) => {
    const fileObj = selectedFiles.find(f => f.id === fileId);
    if (!fileObj) return;

    // Reset error state and start uploading
    setSelectedFiles(prev => {
      const updatedFiles = prev.map(f => 
        f.id === fileId 
          ? { ...f, uploading: true, uploadProgress: 0, error: null }
          : f
      );
      
      // Notify parent component that retry is starting
      onFilesSelected(updatedFiles);
      return updatedFiles;
    });

    const result = await uploadFileDirectly(fileObj.file);
    
    if (result.success) {
      setSelectedFiles(prev => {
        const updatedFiles = prev.map(f => 
          f.id === fileId 
            ? {
                ...f,
                fileKey: result.data.fileKey,
                s3PreviewUrl: null,
                uploading: false,
                uploadProgress: 100,
                uploaded: true,
                error: null
              }
            : f
        );
        
        // Notify parent component of state change
        onFilesSelected(updatedFiles);
        return updatedFiles;
      });
    } else {
      setSelectedFiles(prev => {
        const updatedFiles = prev.map(f => 
          f.id === fileId 
            ? { 
                ...f, 
                uploading: false, 
                uploadProgress: 0,
                error: result.error
              }
            : f
        );
        
        // Notify parent component of state change
        onFilesSelected(updatedFiles);
        return updatedFiles;
      });
    }
  };

  // Close Snackbar
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Get preview URL (S3 if available, otherwise local)
  const getPreviewUrl = (fileObj) => {
    if (fileObj.uploaded && fileObj.s3PreviewUrl) {
      return fileObj.s3PreviewUrl;
    }
    return fileObj.preview;
  };

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* File selection button */}
      <AttachmentUploadButton
        isUploading={false}
        handleAttachmentUpload={handleFileSelection}
      />

      {/* File type/size info box */}
      <Box sx={{
        mt: 2,
        width: '100%',
        bgcolor: '#f1f5fd',
        borderRadius: 2,
        px: 2,
        py: 1.2,
        mb: 1,
      }}>
        <Typography variant="body2" sx={{ color: '#1e40af' }}>
          <b>Supported file types:</b> Images (JPG, PNG, GIF), PDF, Word documents, Excel files, Text files<br/>
          <b>Maximum file size:</b> 5MB per file
        </Typography>
      </Box>

      {/* File preview section with enhanced states */}
      {selectedFiles.length > 0 && (
        <Box sx={{ mt: 3, width: '100%' }}>
          <Typography variant="subtitle2" sx={{ mb: 2, color: '#64748b', fontWeight: 600 }}>
            New Attachments to Upload ({selectedFiles.length})
          </Typography>
          <Grid container spacing={2}>
            {selectedFiles.map((fileObj) => (
              <Grid item xs={12} sm={6} md={4} key={fileObj.id}>
                <EnhancedLocalFileCard
                  fileObj={{
                    ...fileObj,
                    preview: getPreviewUrl(fileObj)
                  }}
                  onRemove={handleRemoveFile}
                  onRetry={handleRetryUpload}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Snackbar */}
      <CustomSnackbar
        open={snackbarOpen}
        message={message}
        severity={severity}
        onClose={handleSnackbarClose}
      />
    </Box>
  );
};

// Enhanced LocalFileCard with progress, error, and retry states
const EnhancedLocalFileCard = ({ fileObj, onRemove, onRetry }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [fileType, setFileType] = useState('');
  const [displayType, setDisplayType] = useState('FILE');
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  
  // Stream scanning status using SSE when fileKey is available, file is uploaded, and fileKey includes "quarantine"
  const isQuarantineFile = fileObj?.fileKey && fileObj.fileKey.includes('quarantine');
  const { scannedStatus, isScanningComplete, isPolling } = useScanningStatusSSE(
    isQuarantineFile && fileObj?.uploaded ? fileObj.fileKey : null,
    isQuarantineFile && fileObj?.uploaded && fileObj?.fileKey // Only stream if file is uploaded, has fileKey, and is in quarantine
  );

  // Handle case where Clean status appears - show for 5 seconds then hide completely
  const [displayStatus, setDisplayStatus] = useState(scannedStatus);
  const [hideStatus, setHideStatus] = useState(false);
  const cleanTimeoutRef = React.useRef(null);
  const cleanHandledRef = React.useRef(false); // Track if CLEAN timeout has been set

  React.useEffect(() => {
    // If status is CLEAN and we haven't handled it yet, set timeout
    if (scannedStatus === 'CLEAN' && !cleanHandledRef.current) {
      setDisplayStatus('CLEAN');
      setHideStatus(false);
      cleanHandledRef.current = true; // Mark as handled
      
      cleanTimeoutRef.current = setTimeout(() => {
        setDisplayStatus(null);
        setHideStatus(true); // Hide status completely after 5 seconds
      }, 5000);
    } else if (scannedStatus === 'INFECTED' || scannedStatus === 'ERROR') {
      // Show INFECTED or ERROR immediately and keep showing
      // Reset clean handled flag if status changes
      if (cleanHandledRef.current) {
        cleanHandledRef.current = false;
        if (cleanTimeoutRef.current) {
          clearTimeout(cleanTimeoutRef.current);
          cleanTimeoutRef.current = null;
        }
      }
      setDisplayStatus(scannedStatus);
      setHideStatus(false);
    } else {
      // For null or other statuses, update display status immediately
      // Reset clean handled flag if status changes away from CLEAN
      if (cleanHandledRef.current && scannedStatus !== 'CLEAN') {
        cleanHandledRef.current = false;
        if (cleanTimeoutRef.current) {
          clearTimeout(cleanTimeoutRef.current);
          cleanTimeoutRef.current = null;
        }
      }
      
      // Only update if we haven't hidden after CLEAN
      if (!hideStatus) {
        setDisplayStatus(scannedStatus);
      } else if (scannedStatus === null) {
        // If we've hidden after CLEAN, keep it hidden for null status
        setDisplayStatus(null);
      } else {
        // If we get a new status after hiding, show it
        setDisplayStatus(scannedStatus);
        setHideStatus(false);
      }
    }

    // Cleanup timeout on unmount
    return () => {
      if (cleanTimeoutRef.current) {
        clearTimeout(cleanTimeoutRef.current);
        cleanTimeoutRef.current = null;
      }
    };
  }, [scannedStatus, hideStatus]);

  React.useEffect(() => {
    if (fileObj?.file) {
      const file = fileObj.file;
      const mimeType = file.type;
      
      // Extract file extension for display
      const extension = file.name.split('.').pop()?.toUpperCase() || 'FILE';
      setFileType(extension);

      // Determine display type based on MIME type
      if (mimeType.startsWith('image/')) {
        setDisplayType('IMAGE');
        setPreviewUrl(fileObj.preview);
      } else if (mimeType === 'application/pdf') {
        setDisplayType('PDF');
      } else if (
        mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        mimeType === 'application/msword'
      ) {
        setDisplayType('DOC');
      } else if (
        mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        mimeType === 'application/vnd.ms-excel' ||
        mimeType === 'text/csv'
      ) {
        setDisplayType('EXCEL');
      } else if (mimeType === 'text/plain') {
        setDisplayType('TEXT');
      } else {
        setDisplayType('DOC');
      }
    } else if (fileObj?.uploaded) {
      // Handle uploaded files without File object
      const fileName = fileObj.name || 'Unknown file';
      const extension = fileName.split('.').pop()?.toUpperCase() || 'FILE';
      setFileType(extension);
      
      // Determine display type based on file type
      const fileType = fileObj.type || '';
      if (fileType.startsWith('image/')) {
        setDisplayType('IMAGE');
        setPreviewUrl(fileObj.preview);
      } else if (fileType === 'application/pdf') {
        setDisplayType('PDF');
      } else if (
        fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        fileType === 'application/msword'
      ) {
        setDisplayType('DOC');
      } else if (
        fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        fileType === 'application/vnd.ms-excel' ||
        fileType === 'text/csv'
      ) {
        setDisplayType('EXCEL');
      } else if (fileType === 'text/plain') {
        setDisplayType('TEXT');
      } else {
        setDisplayType('DOC');
      }
    }
  }, [fileObj]);

  const getFileIcon = () => {
    if (!fileObj?.file && !fileObj?.uploaded) return <DocumentIcon className="w-14 h-14 text-gray-400" />;

    switch (displayType) {
      case 'IMAGE':
        return <PhotoIcon className="w-14 h-14 text-purple-500" />;
      case 'PDF':
        return <DocumentTextIcon className="w-14 h-14 text-red-500" />;
      case 'DOC':
        return <DocumentTextIcon className="w-14 h-14 text-blue-500" />;
      case 'EXCEL':
        return <TableCellsIcon className="w-14 h-14 text-green-500" />;
      case 'TEXT':
        return <DocumentTextIcon className="w-14 h-14 text-gray-500" />;
      default:
        return <DocumentIcon className="w-14 h-14 text-gray-400" />;
    }
  };

  const getChipColor = () => {
    switch (displayType) {
      case 'PDF':
        return { backgroundColor: '#fef2f2', color: '#dc2626' };
      case 'DOC':
        return { backgroundColor: '#eff6ff', color: '#2563eb' };
      case 'EXCEL':
        return { backgroundColor: '#f0fdf4', color: '#16a34a' };
      case 'TEXT':
        return { backgroundColor: '#f8fafc', color: '#64748b' };
      default:
        return { backgroundColor: '#e2e8f0', color: '#475569' };
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleRemove = () => {
    if (onRemove && fileObj?.id) {
      onRemove(fileObj.id);
    }
  };

  const handleRetry = () => {
    if (onRetry && fileObj?.id) {
      onRetry(fileObj.id);
    }
  };

  const handlePreviewOpen = () => {
    setPreviewModalOpen(true);
  };

  const handlePreviewClose = () => {
    setPreviewModalOpen(false);
  };

  return (
    <>
      <Box
        sx={{
          width: 280,
          height: 320,
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          },
          border: fileObj.error ? '1px solid #fca5a5' : '1px solid #e2e8f0',
          borderRadius: 2,
          backgroundColor: 'white',
        }}
      >
        {/* Upload Progress - Circular Loading */}
        {fileObj.uploading && (
          <Box sx={{ 
            p: 2, 
            pb: 1, 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            gap: 1
          }}>
            <CircularProgress 
              size={24} 
              thickness={4}
              sx={{ color: '#7c3aed' }}
            />
            <Typography variant="caption" sx={{ textAlign: 'center', color: '#64748b' }}>
              Uploading...
            </Typography>
          </Box>
        )}

        {/* Error State */}
        {fileObj.error && (
          <Box sx={{ p: 2, pb: 1 }}>
            <Alert 
              severity="error" 
              sx={{ mb: 1 }}
              action={
                <Button size="small" onClick={handleRetry}>
                  Retry
                </Button>
              }
            >
              {fileObj.error}
            </Alert>
          </Box>
        )}

        {/* Success State */}
        {/* {fileObj.uploaded && !fileObj.uploading && (
          <Box sx={{ 
            position: 'absolute', 
            top: 8, 
            left: 8, 
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            borderRadius: 1,
            px: 1,
            py: 0.5
          }}>
            <CheckCircleIcon className="w-4 h-4 text-green-500" />
            <Typography variant="caption" color="success.main" sx={{ fontSize: '0.7rem' }}>
              Uploaded
            </Typography>
          </Box>
        )} */}

        {/* Action buttons - Preview and Remove */}
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1,
            display: 'flex',
            gap: 0.5,
          }}
        >
          {/* Preview: local blob OK while scanning; S3 quarantine URLs only after CLEAN */}
          {fileObj.uploaded && previewUrl && (previewUrl.startsWith('blob:') || scannedStatus === 'CLEAN') && (
            <IconButton
              size="small"
              onClick={handlePreviewOpen}
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                color: '#3b82f6',
                '&:hover': {
                  backgroundColor: '#eff6ff',
                  color: '#2563eb',
                },
              }}
            >
              <EyeIcon style={{ width: 16, height: 16 }} />
            </IconButton>
          )}
          
          {/* Remove button */}
          <IconButton
            size="small"
            onClick={handleRemove}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              color: '#ef4444',
              '&:hover': {
                backgroundColor: '#fef2f2',
                color: '#dc2626',
              },
            }}
          >
            <XMarkIcon style={{ width: 16, height: 16 }} />
          </IconButton>
        </Box>

        {/* Preview section */}
        <Box
          sx={{
            height: 200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: fileObj.error ? '#fef2f2' : '#f8fafc',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {previewUrl && displayType === 'IMAGE' && (previewUrl.startsWith('blob:') || scannedStatus === 'CLEAN') ? (
            // Image preview
            <Box
              component="img"
              src={previewUrl}
              alt={fileObj?.name || 'File preview'}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                cursor: 'pointer',
              }}
              onClick={handlePreviewOpen}
            />
          ) : (
            // Document icon with file type
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
              }}
            >
              {getFileIcon()}
              <Box
                sx={{
                  ...getChipColor(),
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                }}
              >
                {fileType}
              </Box>
            </Box>
          )}
        </Box>

        {/* File details section */}
        <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: fileObj.error ? '#dc2626' : '#1e293b',
              mb: 0.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {fileObj?.name || 'Unknown file'}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
            <Typography
              variant="caption"
              sx={{
                color: '#64748b',
                display: 'block',
              }}
            >
              {formatFileSize(fileObj?.size || 0)}
            </Typography>

            {/* Scanning Status Indicator - Beside file size (only for quarantine files) */}
            {fileObj.uploaded && fileObj.fileKey && fileObj.fileKey.includes('quarantine') && !hideStatus && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {(!["CLEAN", "INFECTED", "ERROR"].includes(displayStatus) && isPolling && !isScanningComplete) && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CircularProgress size={12} sx={{ color: '#d97706' }} />
                    <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#d97706' }}>
                      Scanning...
                    </Typography>
                  </Box>
                )}
                {displayStatus === 'CLEAN' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CheckCircleIcon className="w-3 h-3" style={{ color: '#059669' }} />
                    <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#059669' }}>
                      Scan: Clean
                    </Typography>
                  </Box>
                )}
                {displayStatus === 'INFECTED' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <ExclamationTriangleIcon className="w-3 h-3" style={{ color: '#dc2626' }} />
                    <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#dc2626' }}>
                      Scan: Infected
                    </Typography>
                  </Box>
                )}
                {displayStatus === 'ERROR' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <ExclamationTriangleIcon className="w-3 h-3" style={{ color: '#dc2626' }} />
                    <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#dc2626' }}>
                      Scan: Error
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Preview Modal */}
      {previewModalOpen && previewUrl && (
        <ImagePreviewModal
          open={previewModalOpen}
          onClose={handlePreviewClose}
          imageUrl={previewUrl}
          fileName={fileObj?.name || 'File preview'}
        />
      )}
    </>
  );
};

export default FileSelectionWithPreview;